import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Table,
  Alert,
  Spinner,
  Form,
  InputGroup,
  Badge,
  Row,
  Col,
  Card
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { useAdminTableColumns } from 'api/hooks/use-admin-table-columns'
import { useAdminSystems } from 'api/hooks/use-admin-systems'
import AppBadge from 'shared/badges/app-badge'

const TableColumnsList = () => {
  const { data: tableColumns, isLoading, error } = useAdminTableColumns()
  const { data: systems } = useAdminSystems()
  const [searchTerm, setSearchTerm] = useState('')
  const [moduleFilter, setModuleFilter] = useState('')
  const [displayAsFilter, setDisplayAsFilter] = useState('')
  const [filterTypeFilter, setFilterTypeFilter] = useState('')
  const [systemFilter, setSystemFilter] = useState('')
  const [missingSystemColumn, setMissingSystemColumn] = useState(false)

  // Get unique values for filter dropdowns
  const moduleOptions = useMemo(() => {
    if (!tableColumns) return []
    return [
      ...new Set(tableColumns.map((col) => col.parent_module).filter(Boolean))
    ].sort()
  }, [tableColumns])

  const displayAsOptions = useMemo(() => {
    if (!tableColumns) return []
    return [
      ...new Set(tableColumns.map((col) => col.display_as).filter(Boolean))
    ].sort()
  }, [tableColumns])

  const filterTypeOptions = useMemo(() => {
    if (!tableColumns) return []
    return [
      ...new Set(tableColumns.map((col) => col.filter_type).filter(Boolean))
    ].sort()
  }, [tableColumns])

  const filteredTableColumns = tableColumns?.filter((col) => {
    // Search filter
    const matchesSearch =
      col.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      col.database_column_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      col.section_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      col.parent_module?.toLowerCase().includes(searchTerm.toLowerCase())

    // Module filter
    const matchesModule =
      !moduleFilter ||
      (moduleFilter === 'global' && !col.parent_module) ||
      col.parent_module === moduleFilter

    // Display As filter
    const matchesDisplayAs =
      !displayAsFilter || col.display_as === displayAsFilter

    // Filter Type filter
    const matchesFilterType =
      !filterTypeFilter || col.filter_type === filterTypeFilter

    // Missing System Column filter
    const matchesMissingSystemColumn =
      !missingSystemColumn || col.system_table_columns_count === 0

    // System filter: a column is valid for a system if it has a system
    // table column entry for that system, or if it has no system table
    // column entries at all (treated as unrestricted/global). Mirrors the
    // TableColumn.available_for_system scope on the API.
    const matchesSystem =
      !systemFilter ||
      col.system_table_columns_count === 0 ||
      col.system_ids?.includes(systemFilter)

    return (
      matchesSearch &&
      matchesModule &&
      matchesDisplayAs &&
      matchesFilterType &&
      matchesMissingSystemColumn &&
      matchesSystem
    )
  })

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error loading table columns: {error.message}
      </Alert>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Table Columns</h2>
        <Button as={Link} to="/admin/table-columns/new" variant="primary">
          Create Table Column
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup className="mb-3">
                <InputGroup.Text>Search</InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by name, database column, module, or dataset..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={6}>
              <Form.Select
                className="mb-3"
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
              >
                <option value="">All Modules</option>
                <option value="global">Global (No Module)</option>
                {moduleOptions.map((module) => (
                  <option key={module} value={module}>
                    {module}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Select
                className="mb-3"
                value={displayAsFilter}
                onChange={(e) => setDisplayAsFilter(e.target.value)}
              >
                <option value="">All Display Types</option>
                {displayAsOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Select
                className="mb-3"
                value={filterTypeFilter}
                onChange={(e) => setFilterTypeFilter(e.target.value)}
              >
                <option value="">All Filter Types</option>
                {filterTypeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Select
                className="mb-3"
                value={systemFilter}
                onChange={(e) => setSystemFilter(e.target.value)}
              >
                <option value="">All Systems</option>
                {systems?.map((system) => (
                  <option key={system.id} value={system.id}>
                    {system.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <div>
                  <Form.Check
                    type="checkbox"
                    id="missing-system-column"
                    label="Show only columns without system columns"
                    checked={missingSystemColumn}
                    onChange={(e) => setMissingSystemColumn(e.target.checked)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="mb-3">
        <strong>
          Showing {filteredTableColumns?.length || 0} of{' '}
          {tableColumns?.length || 0} table columns
        </strong>
      </div>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th>Name</th>
            <th>Database Column</th>
            <th>Module</th>
            <th>Dataset</th>
            <th>Display As</th>
            <th>Filter Type</th>
            <th>System Columns</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredTableColumns?.map((column) => (
            <tr key={column.id}>
              <td>{column.name}</td>
              <td>
                <code>{column.database_column_name}</code>
              </td>
              <td>
                {column.parent_module ? (
                  <Badge bg="info">{column.parent_module}</Badge>
                ) : (
                  <Badge bg="secondary">Global</Badge>
                )}
              </td>
              <td>
                {column.section_name ? (
                  column.section_name
                ) : (
                  <span className="text-muted">-</span>
                )}
              </td>
              <td>{column.display_as}</td>
              <td>{column.filter_type}</td>
              <td>
                <Badge
                  bg={
                    column.system_table_columns_count === 0
                      ? 'danger'
                      : 'primary'
                  }
                >
                  {column.system_table_columns_count}
                </Badge>
                {column.system_table_columns_missing_source_count > 0 && (
                  <div className="mt-1">
                    <AppBadge
                      variant="warning"
                      title="System columns missing a Source Table or Source Column"
                    >
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="me-1"
                      />
                      {column.system_table_columns_missing_source_count} missing
                      source
                    </AppBadge>
                  </div>
                )}
              </td>
              <td>
                <Button
                  as={Link}
                  to={`/admin/table-columns/${column.id}`}
                  variant={
                    column.system_table_columns_count === 0 ? 'danger' : 'info'
                  }
                  size="sm"
                  className="me-2"
                >
                  {column.system_table_columns_count === 0 && (
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="me-1"
                    />
                  )}
                  View
                </Button>
                <Button
                  as={Link}
                  to={`/admin/table-columns/${column.id}/edit`}
                  variant="warning"
                  size="sm"
                >
                  Edit
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {filteredTableColumns?.length === 0 && (
        <Alert variant="info">No table columns found.</Alert>
      )}
    </div>
  )
}

export default TableColumnsList
